import { useRef } from 'react';
import { useQuery } from '@apollo/client';

import useCompanyContext from 'common/hooks/useCompanyContext';
import {
  acceptOnlyNonNil,
  throwIfErrorPresent,
} from 'features/Onboarding/utils';

import { OnboardingPageOrState } from '../../legacy/types';
import getApplicationType from '../getApplicationType';
import { applicationTypeQuery } from './graphql';
import {
  ApplicationType,
  ApplicationTypeQueryData,
  ApplicationTypeQueryVariables,
  CurrentApplicationTypeHook,
} from './types';

// Some pages require preemptively setting an application type before the
// correct data has been set.
const pageBasedOverride: Partial<
  Record<OnboardingPageOrState, ApplicationType>
> = {
  'capital-deposit-guide': 'capital-deposit',
};

const getCurrentApplicationType = (
  currentPage: OnboardingPageOrState | null,
  data?: ApplicationTypeQueryData,
) => {
  const currentPageBasedType =
    currentPage === null ? undefined : pageBasedOverride[currentPage];
  if (currentPageBasedType !== undefined) {
    return currentPageBasedType;
  }
  const isShineStart = data?.viewer.company.isShineStart;
  const isCapitalDeposit = data?.viewer.company.isCapitalDeposit;
  return getApplicationType({ isCapitalDeposit, isShineStart });
};

/**
 * Determines which type of account the user is currently applying for.
 * The applicationTypePromise should only be used to determine the first response
 * from the query and will not reflect any changes in the local Apollo state.
 * It will also return the capital deposit and shine start ID for such applications
 * as a helper for making related actions.
 */
const useCurrentApplicationType: CurrentApplicationTypeHook = ({
  currentPage,
}) => {
  const { companyProfileId } = useCompanyContext();
  // TODO(matt): Consider moving this useQuery callback to promise functionality
  // to a separate utility (or use an existing one if appropriate).
  // A reference to the promise's resolver is kept so that it can be called
  // in an imperative fashion when the query has completed.
  const resultPromiseResolverRef = useRef<(result?: ApplicationType) => void>();
  // As above, a reference to the rejector is needed to be called
  // on the event of the query's onError callback being triggered.
  // The rejector is required to accept a reason of 'any' type.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const resultPromiseRejectorRef = useRef<(reason: any) => void>();
  // A reference to the promise keeping track of the result of the query
  // with a stable promise being returned for use by the user of this
  // hook.
  const resultPromiseRef = useRef<Promise<ApplicationType | undefined>>(
    new Promise((resolve, reject) => {
      resultPromiseResolverRef.current = resolve;
      resultPromiseRejectorRef.current = reject;
    }),
  );

  const { data, error, loading } = useQuery<
    ApplicationTypeQueryData,
    ApplicationTypeQueryVariables
  >(applicationTypeQuery, {
    // The two callbacks assume that the resolver/rejector refs have been
    // successfully set by this point and will throw an exception if not
    onCompleted: (queryData) => {
      acceptOnlyNonNil(resultPromiseResolverRef.current)(
        getCurrentApplicationType(currentPage, queryData),
      );
    },

    onError: (queryError) => {
      acceptOnlyNonNil(resultPromiseRejectorRef.current)(queryError);
    },

    skip: companyProfileId === undefined,
    variables: {
      companyProfileId,
    },
  });
  throwIfErrorPresent(error);

  const applicationType = getCurrentApplicationType(currentPage, data);
  const capitalDepositId =
    data?.viewer.company.capitalDeposit?.capitalDepositId;
  const shineStartId = data?.viewer.company.shineStart?.shineStartId;

  return {
    applicationType,
    applicationTypePromise: resultPromiseRef.current,
    capitalDepositId,
    loading,
    shineStartId,
  };
};

// Also export the applicationTypeQuery for updating by other mutations
export { applicationTypeQuery } from './graphql';

export default useCurrentApplicationType;
